import React, { useEffect, useState, useRef } from "react";
import { useRecoilState } from "recoil";
import { MontageSession } from "../../../../State";
import { analytics } from "MVVM/Analytics";

import { MAX_CLIPS } from "../../Components/Clips";
import { TbMoodSad } from "react-icons/tb";

import { LoadingSkeleton } from "../../../../Components/Skeloton";
import { Box, Flex } from "rebass/styled-components";
import { TMontageClip } from "../../@types";
import { Button } from "../../../../Components/Button";
import { Body } from "../../../../Components/Text";
import { Tooltip } from "../../../../Components/Tooltip";
import { Count, CountIndicator } from "../../Components/CountIndicator";
import { ModalClip } from "../../Components/Clips/Components/ModalClip";
import {
  AddButton,
  AddButtonContainer,
  ClipsList,
  ClipsModal,
  FilterContainer,
  Headers,
  ModalContent,
  Select,
  StyledHeading,
} from "./styledComponents";

interface Props {
  isOpen: boolean;
  children?: JSX.Element | JSX.Element[];
  closeClipSelectorModal: () => void;
  clips: TMontageClip[];
  hasClipDemoExpired: (clipId: string) => boolean;
  count: number;
  loading: boolean;
  queryClips: (filter: string[], sort: string) => Promise<void>;
  openProCTA: (modal: { body: string; isOpen: boolean }) => void;
  insertIndex: number;
}

export const Modal: React.FC<Props> = ({
  isOpen,
  closeClipSelectorModal,
  clips,
  loading,
  queryClips,
  hasClipDemoExpired,
}) => {
  const [montageSession, setMontageSession] = useRecoilState(MontageSession);
  const [selectedClipDocs, setSelectedClipDocs] = useState<TMontageClip[]>([]);
  const isMountedRef = useRef(true);

  useEffect(() => {
    if (isOpen) {
      setSelectedClipDocs(montageSession.clips);
    }
  }, [isOpen, montageSession.clips]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const [map, setMap] = useState("All");
  const [kills, setKills] = useState("All");
  const [weapons, setWeapons] = useState("All");
  const [sort, setSort] = useState("createdDate");

  const canProceed = selectedClipDocs.length >= 2;

  const closeModal = () => {
    setSelectedClipDocs([]);
    closeClipSelectorModal();
  };

  useEffect(() => {
    const onFilterApply = async () => {
      if (!isMountedRef.current) return;

      const filter = [];
      if (map !== "All") filter.push(map);
      if (kills !== "All") filter.push(kills);
      if (weapons !== "All") filter.push(weapons);

      queryClips(filter, sort);
    };

    onFilterApply();
  }, [map, weapons, kills, sort]);

  const handleSelect = (clip: TMontageClip) => {
    const selectedClipIds = selectedClipDocs.map((x) => x._id);
    const isSelected = selectedClipIds.includes(clip._id);

    if (!isSelected) {
      analytics.track("Montage Clip Selected", {});
      setSelectedClipDocs([...selectedClipDocs, clip]);
    } else {
      setSelectedClipDocs(
        selectedClipDocs.filter((selectedClip) => {
          return selectedClip._id !== clip._id;
        }),
      );
    }
  };

  const willExceedClipLimit = (clip: TMontageClip) => {
    const selectedClipIds = selectedClipDocs.map((x) => x._id);
    const isAlreadySelected = selectedClipIds.includes(clip._id);

    if (isAlreadySelected) {
      return false;
    }

    return selectedClipDocs.length >= MAX_CLIPS;
  };

  const handleDone = () => {
    closeModal();

    const currentClipIds = montageSession.clips.map(
      (clip: TMontageClip) => clip._id,
    );

    const selectedClipIds = selectedClipDocs.map(
      (clip: TMontageClip) => clip._id,
    );

    const clipsToAdd = selectedClipDocs.filter(
      (clip: TMontageClip) => !currentClipIds.includes(clip._id),
    );

    setMontageSession({
      ...montageSession,
      clips: [
        ...clipsToAdd,
        ...montageSession.clips.filter((clip: TMontageClip) =>
          selectedClipIds.includes(clip._id),
        ),
      ],
    });
  };

  return (
    <ClipsModal isOpen={isOpen} onClick={closeModal}>
      <ModalContent
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Headers mb={2} mt={[4, 4, 4, 0]}>
          <StyledHeading>Show me: </StyledHeading>
          <FilterContainer>
            Map
            <Select defaultValue={map} onChange={(e) => setMap(e.target.value)}>
              <option value="All">All</option>
              <option value="Anubis">Anubis</option>
              <option value="Ancient">Ancient</option>
              <option value="Dust 2">Dust 2</option>
              <option value="Inferno">Inferno</option>
              <option value="Mirage">Mirage</option>
              <option value="Nuke">Nuke</option>
              <option value="Train">Train</option>
              <option value="Agency">Agency</option>
              <option value="Office">Office</option>
              <option value="Vertigo">Vertigo</option>
              <option value="Cache">Cache</option>
              <option value="Overpass">Overpass</option>
            </Select>
            Kills
            <Select
              defaultValue={kills}
              onChange={(e) => setKills(e.target.value)}
            >
              <option value="All">All</option>
              <option value="Kills:2">2</option>
              <option value="Kills:3">3</option>
              <option value="Kills:4">4</option>
              <option value="Kills:5">5</option>
            </Select>
            Weapons
            <Select
              defaultValue={weapons}
              onChange={(e) => setWeapons(e.target.value)}
            >
              <option value="All">All</option>
              <option value="Category:CS_ARS">Assualt Rifles</option>
              <option value="Category:CS_Pistols">Pistols</option>
              <option value="Category:CS_Snipers">Snipers</option>
            </Select>
            Sort
            <Select
              defaultValue={"createdDate"}
              onChange={(e) => setSort(e.target.value)}
            >
              <option value="createdDate">Newest</option>
              <option value="score">Best</option>
              <option value="views">Popular</option>
            </Select>
          </FilterContainer>
        </Headers>
        {!loading && !clips?.length ? (
          <Flex
            style={{
              width: "100%",
              height: "100%",
            }}
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <TbMoodSad size={30} />
            <StyledHeading>No clips with these selected filters</StyledHeading>
          </Flex>
        ) : null}
        <ClipsList>
          {loading && <LoadingClips />}
          {!loading &&
            montageSession?.sequence?.buildDoc &&
            clips?.map((clip: TMontageClip, index: number) => {
              return (
                <Box style={{ width: "99%" }} key={index}>
                  <ModalClip
                    key={clip._id}
                    clip={clip}
                    isSelected={selectedClipDocs.some(
                      (doc) => doc._id === clip._id,
                    )}
                    selected={selectedClipDocs.map((doc) => doc._id)}
                    willExceedClipLimit={willExceedClipLimit(clip)}
                    demoExpired={hasClipDemoExpired(clip._id)}
                    index={index}
                    isFinale={false}
                    handleSelect={handleSelect}
                    forceColumnDisplay={false}
                  />
                </Box>
              );
            })}
        </ClipsList>{" "}
        <AddButtonContainer>
          <Box ml={8}>
            {" "}
            <Tooltip
              position={{
                top: "-65px",
                padding: "7px",
                right: "-188px",
              }}
              type="hover"
              theme="helpTooltip"
              align="right"
              hide={canProceed}
              tooltipContent={
                <>
                  <Flex flexDirection={"column"}>
                    <Box>
                      <Count
                        count={selectedClipDocs.length}
                        max={MAX_CLIPS}
                        canProceed={canProceed}
                      />
                    </Box>
                    <Body>You need at least 2 clips to create a montage</Body>
                  </Flex>
                </>
              }
            >
              <CountIndicator
                heading={"Clips"}
                count={selectedClipDocs.length}
                max={MAX_CLIPS}
                canProceed={canProceed}
              />
            </Tooltip>
          </Box>
          <Box>
            <Button
              type="button"
              size="small"
              flex={true}
              classification="subtle"
              onClick={closeModal}
            >
              Cancel
            </Button>
            <AddButton
              size="small"
              onClick={handleDone}
              classification="roundedFilled"
            >
              Done
            </AddButton>
          </Box>
        </AddButtonContainer>
      </ModalContent>
    </ClipsModal>
  );
};

const LoadingClips = () => {
  return (
    <>
      {[1, 1, 1, 1].map((_, index: number) => {
        return (
          <Flex
            key={index}
            height={"240px"}
            width="335px"
            flexDirection="column"
          >
            <LoadingSkeleton height="194px" width="335px" />
            <Flex pt={2}>
              <LoadingSkeleton height="30px" width="160px" />
            </Flex>
          </Flex>
        );
      })}
    </>
  );
};
